<script setup>
import { computed, ref, watch } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import UnreadBadge from 'dashboard/components-next/Conversation/ConversationCard/UnreadBadge.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
]);

const hovered = ref(false);
const isMobile = useMediaQuery('(max-width: 767px)');
const avatarSize = computed(() => (isMobile.value ? 44 : 56));
const planetBadgeLabel = 'PLANET';

const contactAttributes = computed(
  () => props.currentContact?.additional_attributes || {}
);
const identities = computed(() =>
  Array.isArray(contactAttributes.value.mi_marciana_identities)
    ? contactAttributes.value.mi_marciana_identities
    : []
);
const isInstagramConversation = computed(() => {
  const descriptor = [
    props.inbox?.name,
    props.inbox?.channelType,
    props.inbox?.channel_type,
    props.inbox?.medium,
    props.inbox?.integration_kind,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return descriptor.includes('instagram') && !descriptor.includes('comentario');
});
const isWhatsAppConversation = computed(() => {
  const descriptor = [
    props.inbox?.name,
    props.inbox?.channelType,
    props.inbox?.channel_type,
    props.inbox?.medium,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return descriptor.includes('whatsapp');
});
const isPlanetInbox = computed(() =>
  String(props.inbox?.name || '')
    .toLowerCase()
    .includes('planet')
);
const whatsappPhone = computed(() => props.currentContact?.phone_number || '');
const instagramUsername = computed(() => {
  const value =
    contactAttributes.value.social_instagram_user_name ||
    identities.value.find(identity => identity?.type === 'instagram')?.value;
  return value ? `@${String(value).replace(/^@/, '')}` : '';
});
const hasShopifyIdentity = computed(
  () =>
    Boolean(contactAttributes.value.mi_marciana_shopify_customer_id) ||
    Boolean(contactAttributes.value.mi_marciana_shopify_customers?.length) ||
    identities.value.some(identity => identity?.type === 'shopify')
);
const shopifyCustomerName = computed(() => {
  const names = (contactAttributes.value.mi_marciana_shopify_customers || [])
    .map(customer => customer?.name)
    .filter(Boolean);
  return (
    [...new Set(names)].join(' / ') ||
    contactAttributes.value.mi_marciana_shopify_customer_name ||
    props.currentContact?.name
  );
});
const primaryContactName = computed(() => {
  if (isWhatsAppConversation.value) {
    return hasShopifyIdentity.value
      ? shopifyCustomerName.value
      : whatsappPhone.value;
  }
  if (
    isInstagramConversation.value &&
    !hasShopifyIdentity.value &&
    instagramUsername.value
  ) {
    return instagramUsername.value;
  }
  return props.currentContact?.name;
});
const secondaryWhatsAppPhone = computed(() =>
  isWhatsAppConversation.value &&
  hasShopifyIdentity.value &&
  whatsappPhone.value
    ? `(${whatsappPhone.value})`
    : ''
);
const secondaryInstagramUsername = computed(() =>
  isInstagramConversation.value &&
  hasShopifyIdentity.value &&
  instagramUsername.value
    ? instagramUsername.value
    : ''
);

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const showMetaSection = computed(() => {
  return (
    props.showInboxName ||
    (props.showAssignee && props.assignee.name) ||
    props.chat.priority
  );
});

const isAgentBotAssignee = computed(
  () => props.chat?.meta?.assignee_type === 'AgentBot'
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !props.currentContact?.blocked
);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
  }
);
</script>

<template>
  <div
    class="relative flex items-center flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 cursor-pointer conversation border-b border-n-slate-3 hover:border-n-surface-1 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group hover:z-[1] before:content-[none] before:absolute before:-top-px before:inset-x-0 before:h-px before:bg-n-surface-1 before:pointer-events-none hover:before:content-[''] max-md:!px-2"
    :class="{
      'active animate-card-select bg-n-background !border-n-surface-1':
        isActiveChat,
      'selected bg-n-slate-2 !border-n-surface-1': selected,
      'bg-[#fff4f8] hover:!bg-[#fde7f0] dark:bg-[#321722] dark:hover:!bg-[#40202d]':
        isPlanetInbox && !isActiveChat && !selected,
      'px-0': compact,
      'px-3': !compact,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="avatarSize"
        :inbox="inbox"
        :status="currentContact.availability_status"
        hide-offline-status
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox v-model="selectedModel" />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="px-0 py-3 max-md:py-2 flex-1 min-w-0 border-line">
      <div
        v-if="showMetaSection"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <InboxName v-if="showInboxName" :inbox="inbox" class="flex-1 min-w-0" />
        <div
          class="flex items-baseline gap-2 flex-shrink-0"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <span
            v-if="showAssignee && assignee.name"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center gap-px truncate"
          >
            <Icon
              :icon="
                isAgentBotAssignee ? 'i-lucide-bot' : 'i-lucide-user-round'
              "
              class="size-3 text-n-slate-11 flex-shrink-0"
            />
            <span class="truncate">{{ assignee.name }}</span>
          </span>
          <CardPriorityIcon
            :priority="chat.priority"
            class="flex-shrink-0 !size-3.5"
          />
        </div>
      </div>
      <h4
        class="conversation--user text-sm max-md:text-[13px] my-0 mx-2 pt-0.5 overflow-hidden whitespace-nowrap flex min-w-0 items-center gap-1 ltr:pr-16 rtl:pl-16 text-n-slate-12"
        :class="hasUnread ? 'font-semibold' : 'font-medium'"
      >
        <span class="min-w-0 truncate">{{ primaryContactName }}</span>
        <span
          v-if="isPlanetInbox"
          class="flex-none rounded-full border border-[#e9a9c1] bg-[#fde7f0] px-1.5 text-[9px] font-semibold leading-4 text-[#8b3154]"
        >
          {{ planetBadgeLabel }}
        </span>
      </h4>
      <p
        v-if="secondaryInstagramUsername || secondaryWhatsAppPhone"
        class="mx-2 -mt-0.5 mb-0 truncate text-[11px] leading-4 text-n-slate-10 ltr:pr-16 rtl:pl-16"
      >
        {{ secondaryInstagramUsername || secondaryWhatsAppPhone }}
      </p>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        class="my-0 mx-2 leading-6 h-6 max-md:leading-5 max-md:h-5 flex-1 min-w-0 text-sm max-md:text-[13px] overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="text-n-slate-11 text-sm max-md:text-[13px] my-0 mx-2 leading-6 h-6 max-md:leading-5 max-md:h-5 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3"
        :class="showMetaSection ? 'top-8' : 'top-4'"
      >
        <span class="ml-auto font-normal leading-4 text-xxs">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
          />
        </span>
        <UnreadBadge
          v-if="hasUnread"
          :count="unreadCount"
          class="ltr:ml-auto rtl:mr-auto mt-1"
        />
      </div>
      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        class="mt-0.5 mx-2 mb-0"
      >
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
  </div>
</template>
